import React from "react";
import { Box, Typography } from "@mui/material";
import PhotoIcon from "@mui/icons-material/Photo";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const INSTAGRAM_PINK = "#E1306C";

function InstagramSvgIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="130%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#ig-grad)" />
      <rect x="6" y="6" width="12" height="12" rx="3" fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" fill="none" stroke="white" strokeWidth="1.5" />
      <circle cx="16.5" cy="7.5" r="0.8" fill="white" />
    </svg>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        color: "var(--text-color)",
        opacity: 0.55,
        mb: "8px",
        textTransform: "uppercase",
        letterSpacing: "0.6px",
      }}>
      {children}
    </Typography>
  );
}

function PillOption({ label, Icon, selected, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "6px 12px",
        borderRadius: "20px",
        cursor: "pointer",
        border: selected
          ? `1px solid ${INSTAGRAM_PINK}`
          : "1px solid var(--border-color)",
        backgroundColor: selected ? "rgba(225,48,108,0.1)" : "transparent",
        color: selected ? INSTAGRAM_PINK : "var(--text-color)",
        opacity: selected ? 1 : 0.55,
        fontSize: "12px",
        fontWeight: selected ? 600 : 400,
        transition: "all 0.15s",
        userSelect: "none",
        "&:hover": { opacity: 1, borderColor: INSTAGRAM_PINK, color: INSTAGRAM_PINK },
      }}>
      {Icon && <Icon sx={{ fontSize: 13 }} />}
      {label}
    </Box>
  );
}

const POST_TYPES = [
  { value: "feed", label: "Feed", Icon: PhotoIcon },
  { value: "reel", label: "Reel", Icon: VideoLibraryIcon },
  { value: "story", label: "Story", Icon: AutoStoriesIcon },
];

const HINTS = {
  feed: "1–10 files (photo or video). Multiple files → carousel.",
  reel: "One video only (MP4/MOV). Duration: 3–90 s, aspect ratio 9:16.",
  story: "One file only (photo or video). Caption is ignored by Instagram.",
};

const InstagramSettings = ({ postType = "feed", onChange }) => {
  const [post_type, setPostType] = React.useState(postType);

  React.useEffect(() => {
    if (onChange) {
      onChange({ post_type });
    }
  }, [post_type, onChange]);

  return (
    <Box className="post-card post-card-sm">
      {/* Header */}
      <Box className="post-card-header post-card-header-sm" sx={{ gap: "8px" }}>
        <InstagramSvgIcon size={18} />
        <Typography className="post-card-title">Instagram Settings</Typography>
      </Box>

      <Box
        sx={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}>

        {/* Post Type */}
        <Box>
          <SectionLabel>Post Type</SectionLabel>
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {POST_TYPES.map(({ value, label, Icon }) => (
              <PillOption
                key={value}
                label={label}
                Icon={Icon}
                selected={post_type === value}
                onClick={() => setPostType(value)}
              />
            ))}
          </Box>
        </Box>

        {/* Contextual hint */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "7px" }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 13,
              color: "var(--text-color)",
              opacity: 0.35,
              mt: "2px",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.4,
              lineHeight: 1.55,
            }}>
            {HINTS[post_type]}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default InstagramSettings;
